/**
 * Memory Management Page
 *
 * Tenant-facing memory configuration and monitoring page.
 * Shows memory stats, configuration controls, and data sovereignty options.
 */
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@personic/shared'
import { Brain, Settings, Shield, BarChart3 } from 'lucide-react'
import { MemoryOverview } from './MemoryOverview'
import { MemoryConfig } from './MemoryConfig'
import { MemorySovereignty } from './MemorySovereignty'

const TAB_MAP: Record<string, string> = {
  '/tenant/memory': 'overview',
  '/tenant/memory/config': 'config',
  '/tenant/memory/sovereignty': 'sovereignty',
}

const PATH_MAP: Record<string, string> = {
  overview: '/tenant/memory',
  config: '/tenant/memory/config',
  sovereignty: '/tenant/memory/sovereignty',
}

export default function MemoryManagement() {
  const location = useLocation()
  const navigate = useNavigate()
  const activeTab = useMemo(() => TAB_MAP[location.pathname] || 'overview', [location.pathname])

  const handleTabChange = (tab: string) => {
    const path = PATH_MAP[tab]
    if (path && path !== location.pathname) navigate(path)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Brain className="h-8 w-8 text-purple-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Memory</h1>
          <p className="text-sm text-neutral-400">
            Manage behavioral memory settings and data
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="config" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="sovereignty" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Data Controls
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <MemoryOverview />
        </TabsContent>

        <TabsContent value="config">
          <MemoryConfig />
        </TabsContent>

        <TabsContent value="sovereignty">
          <MemorySovereignty />
        </TabsContent>
      </Tabs>
    </div>
  )
}
